import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { courseService } from '../services/firebase/courses';
import { Course } from '../types/course';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Loader2, RefreshCw, Award } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export const CompletedCoursesPage: React.FC = () => {
  const { user } = useAuth();
  const [completedCourses, setCompletedCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompletedCourses = async () => {
      if (!user) return;
      try {
        setIsLoading(true);
        setError(null);
        const courses = await courseService.getCompletedCourses();
        setCompletedCourses(courses);
      } catch (err) {
        setError('Failed to load completed courses.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompletedCourses();
  }, [user]);

  const handleRestartCourse = async (courseId: string) => {
    if (!window.confirm('Are you sure you want to restart this course? Your progress will be lost.')) {
      return;
    }
    try {
      await courseService.resetCourseProgress(courseId);
      // Refresh the list of completed courses
      const courses = await courseService.getCompletedCourses();
      setCompletedCourses(courses);
    } catch (err) {
      setError('Failed to restart the course.');
      console.error(err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-red-500">{error}</div>;
  }

  return (
    <div className="p-4 sm:p-6">
      <h1 className="text-2xl font-bold mb-6">Completed Courses</h1>
      {completedCourses.length > 0 ? (
        <div className="space-y-4">
          {completedCourses.map(course => (
            <Card key={course.id} className="p-4 sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold">{course.title}</h2>
                  <p className="text-sm text-neutral-600">{course.shortDescription}</p>
                </div>
                <div className="flex flex-col gap-3 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    icon={RefreshCw}
                    onClick={() => handleRestartCourse(course.id)}
                  >
                    Restart Course
                  </Button>
                  <Link to={`/certificate/${course.id}`} className="w-full sm:w-auto">
                    <Button variant="primary" size="sm" fullWidth icon={Award}>
                      View Certificate
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">No Completed Courses Yet</h2>
          <p className="text-neutral-600 mt-2">
            Keep learning to earn your certificates!
          </p>
          <Link to="/courses">
            <Button variant="primary" className="mt-4">
              Explore Courses
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};
